import { Component, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { FirebaseCodeErrorService } from 'src/app/services/firebase-code-error.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom } from 'rxjs';

/** Reglas de complejidad: mayúscula, minúscula, número (minLength lo maneja otro validador) */
function passwordComplexity(): ValidatorFn {
  return (c: AbstractControl): ValidationErrors | null => {
    const v: string = c.value || '';
    if (!v) return null; // que required haga su trabajo
    const errors: any = {};
    if (!/[A-Z]/.test(v)) errors.uppercase = true;
    if (!/[a-z]/.test(v)) errors.lowercase = true;
    if (!/[0-9]/.test(v)) errors.digit = true;
    return Object.keys(errors).length ? errors : null;
  };
}

/** Valida que password y password2 coincidan */
function passwordsMatch(groupName1 = 'password', groupName2 = 'password2'): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const p = group.get(groupName1)?.value;
    const p2 = group.get(groupName2)?.value;
    return p && p2 && p !== p2 ? { mismatch: true } : null;
  };
}

@Component({
    selector: 'app-my-account-password',
    templateUrl: './my-account-password.component.html',
    styleUrls: ['./my-account-password.component.css'],
    standalone: false
})
export class MyAccountPasswordComponent {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private location = inject(Location);
  private _authService = inject(AuthService);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private _firebaseError = inject(FirebaseCodeErrorService);

  error = signal<string>('');
  showTips = signal<boolean>(false);
  isBusy = computed(() => this._loadingService.isLoading());

  form = this.fb.group(
    {
      password: ['', [Validators.required, Validators.minLength(6), passwordComplexity()]],
      password2: ['', [Validators.required]],
    },
    { validators: passwordsMatch() }
  );

  // helpers para pintar requisitos cumplidos
  private pwdVal = computed(() => this.form.controls.password.value ?? '');
  lengthOk = computed(() => this.pwdVal().length >= 6);
  upperOk  = computed(() => /[A-Z]/.test(this.pwdVal()));
  lowerOk  = computed(() => /[a-z]/.test(this.pwdVal()));
  digitOk  = computed(() => /[0-9]/.test(this.pwdVal()));
  mismatch = computed(() => this.form.hasError('mismatch') && (this.form.touched || this.form.dirty));
  showPwd1 = signal(true);
  showPwd2 = signal(true);

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    // Si necesitás (re)cargar usuario/cliente desde backend, hacelo bajo el loader:
    await this._loadingService.run(async () => {
      try {
        // Asegura bootstrap de sesión (idempotente, ok en web/nativo)
        await this._authService.ensureSession();
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
      }
    });
  }

  async submit() {
    this.error.set('');
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const newPass = this.form.controls.password.value!;
    try {
      await this._loadingService.run(async () => {
        // 1) Firebase
        await this._authService.actualizarClaveCurrentUser(newPass);

        // 2) Backend (si seguís guardando x_password en tu API)
        const u = { ...this._usuarioService.usuario(), x_password: newPass };
        await firstValueFrom(this._usuarioService.updateUsuario(u));

        // 3) Volver a mi cuenta
        await this.router.navigate(['/my-account']);
      });
    } catch (e: any) {
      this.error.set(this._firebaseError.codeError(e?.code) || 'No se pudo actualizar la contraseña.');
    }
  }

  togglePwd(which: 1 | 2) {
    if (which === 1) this.showPwd1.update(v => !v);
    else this.showPwd2.update(v => !v);
  }

  back() {
    this.location.back();
  }
}
